import type { ThreeDPreviewService } from "@spiffcommerce/preview";
import { WorkflowExperience } from "..";
import { BundleStateData, BundleStateTransaction, BundleStateTransform } from "../types";

export class BundleStateManager {
    private state: BundleStateData;

    constructor(stateData?: string) {
        this.setState(stateData || `{"transactions": []}`);
    }

    setState(stateData?: string) {
        if (!stateData) {
            return;
        }
        this.state = JSON.parse(stateData);
    }

    getState() {
        return this.state;
    }

    applyStateToWorkflowExperience(workflowExperience: WorkflowExperience) {
        if (!this.state || !workflowExperience?.getWorkflowManager()?.getModelContainer()) {
            return;
        }
        const transaction = this.state.transactions.find(
            (t) => t.transactionId === workflowExperience.getTransaction().id,
        );
        if (!transaction) {
            return;
        }
        const transform = this.getTransformToApply(transaction);
        if (!transform) {
            return;
        }
        const flippedTransform = this.flipTransform(
            transform,
            workflowExperience.getWorkflowManager().getPreviewService()!,
        );
        const modelContainer = workflowExperience.getWorkflowManager().getModelContainer()!;
        modelContainer.position = flippedTransform.position;
        // Data is stored in degrees for consistency with the front-end UI
        modelContainer.rotation = flippedTransform.rotation;
        modelContainer.scale = flippedTransform.scale;
    }

    private getTransformToApply(transaction: BundleStateTransaction): BundleStateTransform | undefined {
        if (transaction.activeTransform && transaction.transforms) {
            return transaction.transforms[transaction.activeTransform];
        }
        return transaction.transform;
    }

    updateWorkflowExperienceTransform(
        workflowExperience: WorkflowExperience,
        transformName: string,
        transform: BundleStateTransform,
    ) {
        const transactionId = workflowExperience.getTransaction().id;
        if (!this.state) {
            this.state = {
                transactions: [],
            };
        } else if (!this.state.transactions) {
            this.state.transactions = [];
        }
        const transactionState = this.state.transactions.find((t) => t.transactionId === transactionId);
        if (transactionState) {
            if (!transactionState.transforms) {
                transactionState.transforms = {};
            }
            transactionState.transforms[transformName] = transform;
        } else {
            this.state.transactions.push({
                transactionId,
                activeTransform: transformName,
                transforms: { [transformName]: transform },
            });
        }
    }

    activateWorkflowExperienceTransform(workflowExperience: WorkflowExperience, transformName: string) {
        if (!workflowExperience?.getWorkflowManager()?.getModelContainer() || !this.state) {
            return;
        }
        const transactionId = workflowExperience.getTransaction().id;
        const transactionState = this.state.transactions.find((t) => t.transactionId === transactionId);
        const transform = transactionState?.transforms?.[transformName];
        if (!transform) {
            return;
        }
        const flippedTransform = this.flipTransform(
            transform,
            workflowExperience.getWorkflowManager().getPreviewService()!,
        );
        const modelContainer = workflowExperience.getWorkflowManager().getModelContainer()!;
        modelContainer.position = flippedTransform.position;
        // Data is stored in degrees for consistency with the front-end UI
        modelContainer.rotation = flippedTransform.rotation;
        modelContainer.scale = flippedTransform.scale;
        transactionState.activeTransform = transformName;
    }

    removeStateForTransaction(transactionId: string) {
        if (!this.state || !this.state.transactions) {
            return;
        }
        const index = this.state.transactions.findIndex((t) => t.transactionId === transactionId);
        if (index !== -1) {
            this.state.transactions.splice(index, 1);
        }
    }

    getSerializedState() {
        if (!this.state) {
            return undefined;
        }
        this.deleteFieldNameFromObjectRecursive(this.state, "__typename");
        return JSON.stringify(this.state);
    }

    private deleteFieldNameFromObjectRecursive(obj: any, fieldName: string) {
        if (typeof obj !== "object" || obj === null) {
            return;
        }
        for (const key in obj) {
            if (key === fieldName) {
                delete obj[key];
            } else {
                this.deleteFieldNameFromObjectRecursive(obj[key], fieldName);
            }
        }
    }

    private flipTransform(transform: BundleStateTransform, previewService: ThreeDPreviewService): BundleStateTransform {
        if (previewService && previewService.flipTransform) {
            const result = previewService.flipTransform(
                transform.position,
                {
                    x: (transform.rotation.x * Math.PI) / 180,
                    y: (transform.rotation.y * Math.PI) / 180,
                    z: (transform.rotation.z * Math.PI) / 180,
                },
                transform.scale,
            );
            return result;
        } else {
            // Retain legacy behavior if preview version is old
            return {
                position: transform.position,
                rotation: {
                    x: (transform.rotation.x * Math.PI) / 180,
                    y: (transform.rotation.y * Math.PI) / 180,
                    z: (transform.rotation.z * Math.PI) / 180,
                },
                scale: transform.scale,
            };
        }
    }
}
